import React, { useEffect } from 'react'
import { Route } from 'react-router-dom'
import {
  PortfolioActivation,
  PortfolioActivationForm,
  PortfolioApprovalRequestSent,
  PortfolioCommission,
  PortfolioCommissionForm,
  PortfolioDecline,
  PortfolioDeclined,
  PortfolioDeclineForm,
  PortfolioInvoicesForm,
  PortfolioPropertyAddress,
  PortfolioPropertyForm,
  PortfolioPropertyOwner,
  PortfolioRenewal,
  PortfolioRentAndFees,
  PortfolioRentAndFeesForm,
  PortfolioSettings,
  PortfolioTenants,
  PortfolioTerm,
  PortfolioTermination,
  PortfolioApplications,
  PortfolioLeaseType,
  PortfolioInvoicesIntro,
} from '../../../containers'
import { DimmerLoader, LeaseTermForm } from '../../../../views/components'
import styles from './PortfolioPage.module.scss'
import { subscribe } from 'react-contextual'
import PortfoliosProvider from '../../../../modules/portfolios/PortfoliosProvider'

/** Slowly moving components into the portfolios module */
import {
  ApplicationsTable,
  LeaseType,
  PartiesTable,
  PortfolioRenewalForm,
  PortfolioSettingsForm,
  PortfolioTerminationForm,
  UnmanagedCommissionForm,
  UnmanagedRentAndFeesForm,
} from '../../../../modules/portfolios/components'
import { $TSFixMe } from 'types/ts-migrate'
import { leaseTypes } from 'modules/api/portfolio/constants'

interface PortfolioPageProps extends $TSFixMe {
  url: string
}

const PortfolioPage = (props: PortfolioPageProps): React.ReactElement => {
  const {
    redirect,
    url,
    portfolioId,
    currentPortfolioId,
    setCurrentPortfolioId,
    isPortfolioViewLoading,
    tenants,
    owners,
    removeLandlordFromLease,
    removeTenantFromLease,
    makeLandlordPrimary,
    makeTenantPrimary,
    addLandlordToLease,
    addTenantToLease,
    portfolioStatus,
    currentLeaseType,
  } = props

  const validateMatched = portfolioId === 'new' || portfolioId.split('-').length === 5

  useEffect(() => {
    if (validateMatched) {
      setCurrentPortfolioId(portfolioId, currentPortfolioId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={styles.root}>
      <DimmerLoader loading={isPortfolioViewLoading} bgColor={'transparent'}>
        <>
          <Route
            strict
            exact
            path={`(${url}|${url}/type)`}
            render={() => (
              <PortfolioLeaseType>
                <LeaseType portfolioId={portfolioId} redirect={redirect} />
              </PortfolioLeaseType>
            )}
          />
          <Route
            strict
            exact
            path={`${url}/property`}
            render={() => (
              <PortfolioPropertyAddress>
                <PortfolioPropertyForm portfolioId={portfolioId} redirect={redirect} />
              </PortfolioPropertyAddress>
            )}
          />
          <Route
            strict
            exact
            path={`${url}/property/owner`}
            render={() => (
              <PortfolioPropertyOwner>
                <PartiesTable
                  portfolioId={portfolioId}
                  partyType="owner"
                  parties={owners}
                  redirect={redirect}
                  removePartyFromLease={removeLandlordFromLease}
                  makePartyPrimary={makeLandlordPrimary}
                  addPartyToLease={addLandlordToLease}
                  portfolioStatus={portfolioStatus}
                />
              </PortfolioPropertyOwner>
            )}
          />
          <Route
            strict
            exact
            path={`${url}/applications`}
            render={() => (
              <PortfolioApplications>
                <ApplicationsTable
                  portfolioId={portfolioId}
                  tenants={tenants}
                  redirect={redirect}
                  portfolioStatus={portfolioStatus}
                />
              </PortfolioApplications>
            )}
          />
          <Route
            strict
            exact
            path={`${url}/tenants`}
            render={() => (
              <PortfolioTenants>
                <PartiesTable
                  portfolioId={portfolioId}
                  partyType="tenant"
                  parties={tenants}
                  redirect={redirect}
                  removePartyFromLease={removeTenantFromLease}
                  makePartyPrimary={makeTenantPrimary}
                  addPartyToLease={addTenantToLease}
                  portfolioStatus={portfolioStatus}
                />
              </PortfolioTenants>
            )}
          />
          <Route
            strict
            exact
            path={`${url}/term`}
            render={() => (
              <PortfolioTerm>
                <LeaseTermForm portfolioId={portfolioId} redirect={redirect} />
              </PortfolioTerm>
            )}
          />
          <Route
            strict
            exact
            path={`${url}/rent-and-fees`}
            render={() => (
              <PortfolioRentAndFees>
                {currentLeaseType === leaseTypes.managed ? <PortfolioRentAndFeesForm /> : <UnmanagedRentAndFeesForm />}
              </PortfolioRentAndFees>
            )}
          />
          <Route
            strict
            exact
            path={`${url}/commission`}
            render={() => (
              <PortfolioCommission>
                {currentLeaseType === leaseTypes.managed ? (
                  <PortfolioCommissionForm />
                ) : (
                  <UnmanagedCommissionForm portfolioId={portfolioId} redirect={redirect} />
                )}
              </PortfolioCommission>
            )}
          />
          <Route
            strict
            exact
            path={`${url}/invoices`}
            render={() => (
              <PortfolioInvoicesIntro>
                <PortfolioInvoicesForm />
              </PortfolioInvoicesIntro>
            )}
          />
          <Route
            strict
            exact
            path={`${url}/settings`}
            render={() => (
              <PortfolioSettings>
                <PortfolioSettingsForm />
              </PortfolioSettings>
            )}
          />
          <Route
            strict
            exact
            path={`${url}/activation`}
            render={() => (
              <PortfolioActivation>
                <PortfolioActivationForm portfolioId={portfolioId} />
              </PortfolioActivation>
            )}
          />
          <Route strict exact path={`${url}/approval-sent`} render={() => <PortfolioApprovalRequestSent />} />
          <Route
            strict
            exact
            path={`${url}/decline`}
            render={() => (
              <PortfolioDecline>
                <PortfolioDeclineForm />
              </PortfolioDecline>
            )}
          />
          <Route strict exact path={`${url}/declined`} render={() => <PortfolioDeclined />} />
          <Route
            strict
            exact
            path={`${url}/renewal`}
            render={() => (
              <PortfolioRenewal>
                <PortfolioRenewalForm portfolioId={portfolioId} />
              </PortfolioRenewal>
            )}
          />
          <Route
            strict
            exact
            path={`${url}/termination`}
            render={() => (
              <PortfolioTermination>
                <PortfolioTerminationForm />
              </PortfolioTermination>
            )}
          />
        </>
      </DimmerLoader>
    </div>
  )
}

export default subscribe(
  [PortfoliosProvider],
  ({
    redirect,
    isPortfolioViewLoading,
    tenants,
    owners,
    removeLandlordFromLease,
    removeTenantFromLease,
    makeLandlordPrimary,
    makeTenantPrimary,
    addLandlordToLease,
    addTenantToLease,
    portfolioStatus,
    currentLeaseType,
  }: any) => ({
    redirect,
    isPortfolioViewLoading,
    tenants,
    owners,
    removeLandlordFromLease,
    removeTenantFromLease,
    makeLandlordPrimary,
    makeTenantPrimary,
    addLandlordToLease,
    addTenantToLease,
    portfolioStatus,
    currentLeaseType,
  }),
)(PortfolioPage)
